<template>
  <div>
    <v-row dense>
      <v-col class="py-2" cols="12">
        <qtm-input-label label="First Name" required>
          <qtm-text-field
            v-model="v$.contact.first_name.$model"
            data-test="contact-first-name-input"
            :disabled="disabled"
            :error-messages="errors.contact.first_name"
            hide-details="auto"
            :maxlength="CONTACT_MAX_NAME_LENGTH"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2" cols="12">
        <qtm-input-label label="Last Name">
          <qtm-text-field
            v-model="contact.last_name"
            data-test="contact-last-name-input"
            :disabled="disabled"
            hide-details
            :maxlength="CONTACT_MAX_NAME_LENGTH"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2" cols="8">
        <qtm-input-label label="Phone">
          <phone-input
            v-model="v$.contact.phone_number.$model"
            data-test="contact-phone-input"
            :disabled="disabled"
            :error="!!errors.contact.phone_number"
            hide-details="auto"
          />
        </qtm-input-label>
      </v-col>
      <v-col class="py-2" cols="4">
        <qtm-input-label label="Phone Ext.">
          <qtm-text-field
            v-model="contact.phone_extension"
            :disabled="disabled"
            data-test="contact-phone-extension-input"
            hide-details
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2" cols="12">
        <qtm-input-label label="Email" :required="emailRequired">
          <qtm-text-field
            v-model.trim="v$.contact.email.$model"
            data-test="contact-email-input"
            :disabled="disabled"
            :error-messages="errors.contact.email"
            hide-details="auto"
            style="max-width: 450px"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row v-if="alternativeContacts.length" dense>
      <v-col class="py-2" cols="12">
        <qtm-input-label label="Alternative contacts">
          <qtm-autocomplete
            :model-value="contact.alternative_contacts"
            data-test="contact-alternative-contacts-input"
            chips
            closable-chips
            hide-details
            item-value="id"
            :item-title="(c) => `${c.first_name} ${c.last_name} (${c.email})`"
            :items="alternativeContacts"
            multiple
            style="max-width: 450px"
            variant="outlined"
            @update:model-value="setAlternativeContacts"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { email, required, requiredIf } from '@vuelidate/validators'
import type { Contact } from '@quotetome/materials-api'
import PhoneInput from '@/components/phone-input.vue'
import useValidation from '@/composables/validation'
import { CONTACT_MAX_NAME_LENGTH, phoneNumber } from '@/constants'

export interface Props {
  contact: Contact
  disabled?: boolean
  emailRequired?: boolean
  vendorContacts?: Contact[]
}

const props = withDefaults(defineProps<Props>(), {
  vendorContacts: () => []
})

const rules = computed(() => ({
  contact: {
    first_name: { required },
    email: {
      email,
      required: requiredIf(() => !!props.emailRequired),
    },
    phone_number: { phoneNumber },
  },
}))

const { errors, isValid, v$ } = useValidation({
  errorsByKey: { contact: { email: 'Please use a valid email address' } },
  rules,
  state: toRefs(props),
})

const alternativeContacts = computed(() =>
  props.vendorContacts.filter(c => c.id !== props.contact.id && c.verified)
)

const setAlternativeContacts = (value: Contact['id'][]) => {
  props.contact.alternative_contacts = props.vendorContacts?.filter(c => value.includes(c.id))
}

const resetValidation = () => v$.value.$reset()

defineExpose({ isValid, resetValidation })
</script>
